import React, { useState, useEffect } from 'react';
import { Button, Card, Alert, Dropdown } from 'react-bootstrap';
import { useAccount, useReadContract } from 'wagmi';
import { writeContract, waitForTransactionReceipt } from '@wagmi/core';
import communitiesContractABI from '../contracts/tgetherCommunities.json';
import membersContractABI from '../contracts/tgetherMembers.json';
import communityEnrollmentABI from '../contracts/CommunityEnrollment.json';
import CreateCommunityModal from './FormModals/CreateCommunityModal.tsx';
import CommunityProposalModal from './FormModals/CommunityProposalModal.tsx';
import InviteModal from './FormModals/InviteModal.tsx';
import { useNavigate } from 'react-router-dom';
import * as configs from '../config.tsx';
import ConsensusParams from './ParamTypes/ConsensusParams.tsx';
import IncentiveParams from './ParamTypes/IncentiveParams.tsx';
import ParameterGroupComponent from './ParameterGroupComponent.tsx';
const communitiesContractAddress = process.env.REACT_APP_COMMUNITIES_CONTRACT_ADDRESS as `0x${string}`;
const membersContractAddress = process.env.REACT_APP_MEMBERS_CONTRACT_ADDRESS as `0x${string}`;
const enrollmentContractAddress = process.env.REACT_APP_COMMUNITY_ENROLLMENT_CONTRACT_ADDRESS as `0x${string}`; // Enrollment contract address

const parameterComponents = {
  Consensus: ConsensusParams,
  Incentive: IncentiveParams,
};

interface CommunityInfoType {
  minCredsToProposeVote: number;
  minCredsToVote: number;
  maxCredsCountedForVote: number;
  minProposalVotes: number;
  membersAccessContract: string;
  proposalTime: number;
  proposalDelay: number;
  isInviteOnly: boolean;
}

function CommunitySearch({ value }: { value: string }) {
  const { address: account } = useAccount();
  const [communityInfo, setCommunityInfo] = useState<CommunityInfoType | null>(null);
  const [communityExists, setCommunityExists] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteButton, setShowInviteButton] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCommunityProposalModal, setShowCommunityProposalModal] = useState(false);
  const [selectedParameterType, setSelectedParameterType] = useState<string | null>(null);
  const [parameterGroups, setParameterGroups] = useState<string[] | null>(null);

  const ParameterComponent = selectedParameterType ? parameterComponents[selectedParameterType] : null;
  const navigate = useNavigate();

  // Fetch if community exists
  const { data: existsData, isSuccess: existsSuccess } = useReadContract({
    address: communitiesContractAddress,
    abi: communitiesContractABI.abi,
    functionName: 'getCommunityExists',
    args: [value],
  });

  // Fetch community-enrolled parameter groups
  const { data: parameterGroupsData, isSuccess: parameterGroupsSuccess, isError, error } = useReadContract({
    address: enrollmentContractAddress,
    abi: communityEnrollmentABI.abi, 
    functionName: 'getCommunityParameterGroups',
    args: [value],
  });

  // Fetch membership status
  const { data: memberData, isSuccess: memberSuccess } = useReadContract({
    address: membersContractAddress,
    abi: membersContractABI.abi,
    functionName: 'getIsMember',
    args: [account, value],
  });

  // Fetch ownership status
  const { data: ownerData, isSuccess: ownerSuccess } = useReadContract({
    address: communitiesContractAddress,
    abi: communitiesContractABI.abi,
    functionName: 'getCommunityOwner',
    args: [value],
  });

  // Fetch community details
  const { data: communityData, isSuccess: communitySuccess } = useReadContract({
    address: communitiesContractAddress,
    abi: communitiesContractABI.abi,
    functionName: 'communities',
    args: [value],
  });

  // Set community existence state
  useEffect(() => {
    if (existsSuccess) {
      setCommunityExists(Boolean(existsData));
      setLoading(false);
    }
  }, [existsSuccess, existsData]);

  // Set community-enrolled parameter groups state
  useEffect(() => {

    if (parameterGroupsSuccess) {
        const groups = (parameterGroupsData as string[]).filter(group => group !== '');
        setParameterGroups(groups);
    } else if (isError) {
      console.error('Error fetching parameter groups:', error);
    }
  }, [parameterGroupsSuccess, parameterGroupsData, isError, error]);

  // Set community info state
  useEffect(() => {
    if (communitySuccess && communityData) {
      const [
        ownerAddress,
        minCredsToProposeVote,
        minCredsToVote,
        maxCredsCountedForVote,
        minProposalVotes,
        membersAccessContract,
        proposalTime,
        proposalDelay,
        isInviteOnly,
      ] = communityData as any;

      setCommunityInfo({
        minCredsToProposeVote: Number(minCredsToProposeVote),
        minCredsToVote: Number(minCredsToVote),
        maxCredsCountedForVote: Number(maxCredsCountedForVote),
        minProposalVotes: Number(minProposalVotes),
        membersAccessContract: membersAccessContract !== '0x0000000000000000000000000000000000000000'
          ? membersAccessContract
          : 'DEFAULT',
        proposalTime: Number(proposalTime),
        proposalDelay: Number(proposalDelay),
        isInviteOnly: Boolean(isInviteOnly),
      });
    }
  }, [communitySuccess, communityData]);

  // Set membership and ownership status
  useEffect(() => {
    if (memberSuccess) setIsMember(Boolean(memberData));
    if (ownerSuccess && account) setIsOwner(ownerData === account);
  }, [memberSuccess, memberData, ownerSuccess, ownerData, account]);

  // Handle showing the invite button
  useEffect(() => {
    if (communityInfo && isOwner && communityInfo.isInviteOnly) {
      setShowInviteButton(true);
    }
  }, [communityInfo, isOwner]);

  const handleBecomeMember = async () => {
    try {
      const tx = await writeContract(configs.config, {
        address: membersContractAddress,
        abi: membersContractABI.abi,
        functionName: 'addSelfAsMember',
        args: [value],
      });
      await waitForTransactionReceipt(configs.config, { hash: tx });
      navigate(`/search-results?type=Community&value=${value}`);
    } catch (error) {
      if (error.message && error.message.includes('You are not invited to this community.')) {
        alert('You are not invited to this community.');
      } else {
        alert('An error occurred while trying to become a member. Please try again.');
        console.error('Error checking membership status:', error);
      }
    }
  };

  const handleRemoveMember = async () => {
    try {
      const tx = await writeContract(configs.config, {
        address: membersContractAddress,
        abi: membersContractABI.abi,
        functionName: 'removeSelfAsMember',
        args: [value],
      });
      await waitForTransactionReceipt(configs.config, { hash: tx });
      navigate(`/search-results?type=Community&value=${value}`);
    } catch (error) {
      console.error('Error removing from membership:', error);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      {communityExists ? (
        <Card style={{ minWidth: '100%' }}>
          <Card.Body>
            <Card.Title style={{ position: 'relative', textAlign: 'center' }}>
              <div className="community-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <h2 className="text-info">{value}</h2>
                <div style={{ alignSelf: 'flex-end', width: '100%' }}>
                  {!isMember ? (
                    <Button variant="success" onClick={handleBecomeMember}>
                      Become a Member
                    </Button>
                  ) : (
                    <Button variant="danger" onClick={handleRemoveMember}>
                      Remove Self as Member
                    </Button>
                  )}
                </div>
              </div>
              <div style={{ marginTop: '10px' }}>
                <span style={{ color: isOwner ? 'green' : 'red' }}>{isOwner ? '✔️ Is Owner' : '❌ Is Owner'}</span>
                <br />
                <span style={{ color: isMember ? 'green' : 'red' }}>{isMember ? '✔️ Is Member' : '❌ Is Member'}</span>
              </div>
              <div>
                {isOwner && communityInfo?.isInviteOnly && (
                  <Button variant="info" onClick={() => setShowInviteModal(true)}>
                    Invite/Uninvite Member
                  </Button>
                )}
              </div>
            </Card.Title>
            <Card
              style={{
                margin: '20px auto',
                padding: '20px',
                maxWidth: '600px',
                backgroundColor: '#1f1f1f',
                borderRadius: '8px',
                color: '#ffffff',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              }}
            >
              <h5
                style={{
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  backgroundColor: '#2a2a2a',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '4px',
                  textAlign: 'center',
                }}
              >
                Community Parameters
              </h5>
              {communityInfo ? (
                <div style={{ lineHeight: '1.8' }}>
                  <Card.Text>
                    <strong>Min Creds to Propose Vote:</strong>{' '}
                    <span className="badge bg-info">{communityInfo.minCredsToProposeVote}</span>
                  </Card.Text>
                  <Card.Text>
                    <strong>Min Creds to Vote:</strong>{' '}
                    <span className="badge bg-success">{communityInfo.minCredsToVote}</span>
                  </Card.Text>
                  <Card.Text>
                    <strong>Max Creds Counted for Vote:</strong>{' '}
                    <span className="badge bg-warning">{communityInfo.maxCredsCountedForVote}</span>
                  </Card.Text>
                  <Card.Text>
                    <strong>Min Proposal Votes:</strong>{' '}
                    <span className="badge bg-danger">{communityInfo.minProposalVotes}</span>
                  </Card.Text>
                  <Card.Text>
                    <strong>Members Access Contract:</strong>{' '}
                    <span style={{ color: '#f0ad4e' }}>{communityInfo.membersAccessContract}</span>
                  </Card.Text>
                  <Card.Text>
                    <strong>Proposal Time:</strong>{' '}
                    <span className="badge bg-secondary">{communityInfo.proposalTime}</span>
                  </Card.Text>
                  <Card.Text>
                    <strong>Proposal Delay:</strong>{' '}
                    <span className="badge bg-primary">{communityInfo.proposalDelay}</span>
                  </Card.Text>
                  <Card.Text>
                    <strong>Invite Only:</strong>{' '}
                    <span style={{ color: communityInfo.isInviteOnly ? 'limegreen' : 'crimson' }}>
                      {communityInfo.isInviteOnly ? 'Yes' : 'No'}
                    </span>
                  </Card.Text>
                </div>
              ) : (
                <Card.Text>Community data is loading...</Card.Text>
              )}
              <Button variant="info" style={{ marginTop: '20px', width: '100%' }} onClick={() => setShowCommunityProposalModal(true)}>
                Create Community Proposal
              </Button>
              <CommunityProposalModal show={showCommunityProposalModal} setShow={() => setShowCommunityProposalModal(false)} communityName={value} />
            </Card>

            <div className="community-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Dropdown onSelect={(type) => setSelectedParameterType(type)}>
                <Dropdown.Toggle variant="warning" id="dropdown-basic">
                  {selectedParameterType || 'Select Parameter Type'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {Object.keys(parameterComponents).map((type) => (
                    <Dropdown.Item eventKey={type} key={type}>
                      {type === 'Consensus' ? 'Consensus Parameters' : null}
                      {type === 'Incentive' ? 'Incentive Parameters' : null}
                    </Dropdown.Item>
                  ))}
                  {parameterGroups && parameterGroups.map((group) => (
                    <Dropdown.Item eventKey={group} key={group}>
                      {group}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div style={{ marginTop: '20px' }}>
              {selectedParameterType === 'Consensus' && <ConsensusParams community={value} isOwner={isOwner} />}
              {selectedParameterType === 'Incentive' && <IncentiveParams community={value} isOwner={isOwner} />}
              {selectedParameterType != 'Incentive' && selectedParameterType != 'Consensus' && selectedParameterType && <ParameterGroupComponent community={value} groupName= {selectedParameterType!} isOwner={isOwner} />}

              {/* Additional logic to load custom parameter groups */}
            </div>

            <InviteModal show={showInviteModal} setShow={setShowInviteModal} communityName={value} />
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="warning">
          Community "{value}" does not exist.
          <br />
          <Button variant="info" onClick={() => setShowCreateModal(true)}>
            Create Community
          </Button>
          <CreateCommunityModal show={showCreateModal} setShow={setShowCreateModal} />
        </Alert>
      )}
    </div>
  );
}

export default CommunitySearch;
